/**
 * @Description 评分概要(包含预测营业额) 五维、六维评分时使用
 */

import { FC, useMemo, useRef } from 'react';
import { Typography } from 'antd';
import IconFont from '@/common/components/IconFont';
import SiteMapExplain from '@/common/components/business/SiteMap/SiteMapExplain';
import ScoreFiveOwnScore from '../ScoreFiveOwnScore';
import ScoreFourOwnScore from '../ScoreFourOwnScore'; // 只展示一个营业额预测时使用四维时同样的横向展示样式
import TotalSalesAmountPredict from '../TotalSalesAmountPredict';
import { XTCScoreType } from '../../ts-config';
import styles from './index.module.less';
import cs from 'classnames';
import { isNotEmpty } from '@lhb/func';
import { fullEditorCss } from '@/common/components/config-v2';

const { Text } = Typography;

interface ScoreSummaryProps {
  overviewData: any;
  predictSale?: boolean;
  configSwitch: any;
  shareParams?:Object;// 分享页数据
  /** 是否导出pdf */
  isPdf?:boolean;
  detail?: any;
}
const ScoreFive: FC<ScoreSummaryProps> = ({
  overviewData,
  predictSale = false,
  configSwitch,
  shareParams = {},
  isPdf = false,
  detail,
}) => {
  const shadowRef: any = useRef();
  const showTotalScore = !!configSwitch?.showTotalScore; // 是否显示总分
  const showBenefit = !!configSwitch?.showIndustryBenefitContent; // 是否展示有利于
  const showXtcScore = isNotEmpty(overviewData?.category1SalesAmountPredict); // 是否展示xtc评分
  const showBbgScore = isNotEmpty(overviewData?.category2SalesAmountPredict); // 是否展示bbg评分
  const showNormalScore = !showXtcScore && !showXtcScore && isNotEmpty(overviewData?.salesAmountPredict); // 是否展示原始类型
  const showBothScore = showXtcScore && showBbgScore;

  const evaluateInfoStr = useMemo(() => {
    const { marketScore, firstLevelName } = overviewData || {};
    if (marketScore > 70) {
      return `有利于${firstLevelName || ''}经营`;
    }
    if (marketScore > 50 && marketScore <= 70) {
      return `经营${firstLevelName || ''}一般`;
    }
    if (marketScore <= 50) {
      return `不利于${firstLevelName || ''}经营`;
    }
    return '-';
  }, [overviewData]);

  const showScoreDescriptionFlag = useMemo(() => {
    // 1：默认值，2：自定义富文本，3：不展示
    // 如果没有这个参数，也就是没设置过，就沿用老逻辑展示默认值。
    if (!isNotEmpty(detail.showScoreDescriptionFlag)) {
      return 1;
    } else {
      return detail.showScoreDescriptionFlag;
    }
  }, [detail.showScoreDescriptionFlag]);
  return (
    <div className={styles.scoreSummary}>
      {showTotalScore && <div className={styles.scoreSummaryHeader}>
        <div className={styles.totalScoreWrapper}>
          <div className={styles.scoreTitle}>评分</div>
          <div className={styles.totalScoreInfo}>
            <div className={cs(styles.totalScoreVal, 'mr-4')}>{overviewData?.marketScore}</div>
            {[1, 2].includes(showScoreDescriptionFlag) && !isPdf && <div>
              <IconFont
                iconHref='iconxq_ic_shuoming_normal'
                className='mr-2 c-666 fs-12'
                tooltipConfig={{
                  placement: 'bottom',
                  overlayInnerStyle: {
                    width: '380px',
                    padding: '16px',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                  },
                  color: '#fff',
                  onOpenChange(open) {
                    // left: 737px; top: 137px; transform-origin: 50% -4px;
                    if (showScoreDescriptionFlag !== 1 && detail.scoreDescription && open) {
                      setTimeout(() => {
                        // 创建Shadow DOM根节点
                        const shadowRoot = shadowRef.current.attachShadow({ mode: 'open' });
                        const contentDiv = document.createElement('div');
                        contentDiv.innerHTML = detail.scoreDescription;
                        // 创建样式
                        const style = document.createElement('style');
                        style.textContent = fullEditorCss;
                        // 添加到Shadow DOM
                        shadowRoot.appendChild(style);
                        shadowRoot.appendChild(contentDiv);
                      }, 1);
                    }
                  },
                  title: (
                    showScoreDescriptionFlag === 1 ? (
                      <SiteMapExplain
                        markType={overviewData?.markType}
                        isPredictSale={predictSale}
                        shareParams={shareParams}
                      />
                    ) : (
                      <div ref={shadowRef}></div>
                    )
                  ),
                }}
              />
            </div>}
            { showBenefit ? <div className='c-222 fs-12'>
              <Text
                style={{ maxWidth: '180px' }}
                ellipsis={{ tooltip: evaluateInfoStr }}
                className='c-222 fs-12'
              >
                <span>({evaluateInfoStr})</span>
              </Text>
            </div> : <></> }
          </div>
        </div>
        <TotalSalesAmountPredict
          category1SalesAmountPredict={overviewData?.category1SalesAmountPredict}
          category2SalesAmountPredict={overviewData?.category2SalesAmountPredict}
        />
      </div>}

      {/*
        xtc评分+预测营业额、bbg评分+预测营业额
        两个都展示时，展示竖直排版定制组件
        只展示一个时，展示横向排版定制组件
      */}
      {showBothScore ? (<div className={cs(styles.xtcScoresRow, 'mt-12')}>
        <ScoreFiveOwnScore
          scoreType={XTCScoreType.XTC}
          score={overviewData?.category1MarketScore}
          timeDimension={5}
          salesAmountPredict={overviewData?.category1SalesAmountPredict}
          salesPredict={overviewData?.category1SalesPredict}
        />
        <ScoreFiveOwnScore
          scoreType={XTCScoreType.BBG}
          score={overviewData?.category2MarketScore}
          timeDimension={5}
          salesAmountPredict={overviewData?.category2SalesAmountPredict}
          salesPredict={overviewData?.category2SalesPredict}
          wrapperClassName='mt-8'
        />
      </div>) : showXtcScore ? (<ScoreFourOwnScore
        timeDimension={5}
        salesAmountPredict={overviewData?.category1SalesAmountPredict}
        showPredictIcon={true}
        salesPredict={overviewData?.category1SalesPredict}
        wrapperClassName={'mt-20'}
      />) : showBbgScore ? (<ScoreFourOwnScore
        timeDimension={5}
        salesAmountPredict={overviewData?.category2SalesAmountPredict}
        showPredictIcon={true}
        salesPredict={overviewData?.category2SalesPredict}
        wrapperClassName={'mt-20'}
      />) : (showNormalScore ? <ScoreFourOwnScore
        timeDimension={5}
        salesAmountPredict={overviewData?.salesAmountPredict}
        showPredictIcon={true}
        salesPredict={overviewData?.salesPredict}
        wrapperClassName={'mt-20'}
      /> : <></>)}

      {/* 占位图片，固定在右下角 */}
      <div className={styles.imgWrapper}>
        <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/selectionmap/icon_score.png`} width='100%' height='100%'/>
      </div>
    </div>
  );
};

export default ScoreFive;
